import { LeaderboardEntry, LeaderboardData, GAME_LIST } from '../types/game.types';

export class LeaderboardManager {
  private static readonly STORAGE_KEY = 'c64-arcade-leaderboard';
  private static readonly MAX_ENTRIES_PER_GAME = 20;
  private static readonly MAX_ALL_TIME_ENTRIES = 50;
  private static instance: LeaderboardManager | null = null;
  
  private data: LeaderboardData;

  private constructor() {
    this.data = this.loadFromStorage();
  }

  public static getInstance(): LeaderboardManager {
    if (!LeaderboardManager.instance) {
      LeaderboardManager.instance = new LeaderboardManager();
    }
    return LeaderboardManager.instance;
  }

  private loadFromStorage(): LeaderboardData {
    try {
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        const stored = localStorage.getItem(LeaderboardManager.STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          return this.validateAndMigrateData(parsed);
        }
      }
    } catch (error) {
      console.warn('Failed to load leaderboard data from storage:', error);
    }
    
    return this.createEmptyData();
  }

  private validateAndMigrateData(data: any): LeaderboardData {
    const result: LeaderboardData = {
      gameLeaderboards: {},
      allTimeLeaderboard: []
    };

    // Validate game leaderboards
    if (data.gameLeaderboards && typeof data.gameLeaderboards === 'object') {
      GAME_LIST.forEach(game => {
        const gameEntries = data.gameLeaderboards[game.id];
        if (Array.isArray(gameEntries)) {
          result.gameLeaderboards[game.id] = gameEntries
            .filter(this.isValidEntry)
            .map(this.sanitizeEntry)
            .sort((a: LeaderboardEntry, b: LeaderboardEntry) => b.score - a.score)
            .slice(0, LeaderboardManager.MAX_ENTRIES_PER_GAME);
        } else {
          result.gameLeaderboards[game.id] = [];
        }
      });
    } else {
      GAME_LIST.forEach(game => {
        result.gameLeaderboards[game.id] = [];
      });
    }

    // Validate all-time leaderboard
    if (Array.isArray(data.allTimeLeaderboard)) {
      result.allTimeLeaderboard = data.allTimeLeaderboard
        .filter(this.isValidEntry)
        .map(this.sanitizeEntry)
        .sort((a: LeaderboardEntry, b: LeaderboardEntry) => b.score - a.score)
        .slice(0, LeaderboardManager.MAX_ALL_TIME_ENTRIES);
    }

    return result;
  }

  private isValidEntry(entry: any): boolean {
    return (
      entry &&
      typeof entry.initials === 'string' &&
      entry.initials.length <= 3 &&
      typeof entry.score === 'number' &&
      entry.score >= 0 &&
      typeof entry.gameId === 'string' &&
      typeof entry.timestamp === 'number'
    );
  }

  private sanitizeEntry(entry: any): LeaderboardEntry {
    return {
      initials: entry.initials.substring(0, 3).toUpperCase(),
      score: Math.floor(Math.abs(entry.score)),
      gameId: entry.gameId,
      timestamp: entry.timestamp,
      level: entry.level ? Math.floor(Math.abs(entry.level)) : undefined
    };
  }

  private createEmptyData(): LeaderboardData {
    const data: LeaderboardData = {
      gameLeaderboards: {},
      allTimeLeaderboard: []
    };

    GAME_LIST.forEach(game => {
      data.gameLeaderboards[game.id] = [];
    });

    return data;
  }

  private saveToStorage(): void {
    try {
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        const serialized = JSON.stringify(this.data);
        localStorage.setItem(LeaderboardManager.STORAGE_KEY, serialized);
      }
    } catch (error) {
      console.error('Failed to save leaderboard data to storage:', error);
    }
  }

  public addScore(gameId: string, initials: string, score: number, level?: number): boolean {
    // Validate input
    if (!gameId || !initials || score < 0) {
      console.warn('Invalid score data provided');
      return false;
    }

    // Check if game exists
    const gameExists = GAME_LIST.some(game => game.id === gameId);
    if (!gameExists) {
      console.warn(`Unknown game ID: ${gameId}`);
      return false;
    }

    // Create new entry
    const entry: LeaderboardEntry = {
      initials: initials.substring(0, 3).toUpperCase(),
      score: Math.floor(score),
      gameId,
      timestamp: Date.now(),
      level: level ? Math.floor(level) : undefined
    };

    // Add to game-specific leaderboard
    if (!this.data.gameLeaderboards[gameId]) {
      this.data.gameLeaderboards[gameId] = [];
    }

    this.data.gameLeaderboards[gameId].push(entry);
    this.data.gameLeaderboards[gameId].sort((a, b) => b.score - a.score);
    this.data.gameLeaderboards[gameId] = this.data.gameLeaderboards[gameId]
      .slice(0, LeaderboardManager.MAX_ENTRIES_PER_GAME);

    // Add to all-time leaderboard
    this.data.allTimeLeaderboard.push(entry);
    this.data.allTimeLeaderboard.sort((a, b) => b.score - a.score);
    this.data.allTimeLeaderboard = this.data.allTimeLeaderboard
      .slice(0, LeaderboardManager.MAX_ALL_TIME_ENTRIES);

    // Save to storage
    this.saveToStorage();

    return true;
  }

  public isHighScore(gameId: string, score: number): boolean {
    const gameEntries = this.data.gameLeaderboards[gameId] || [];
    
    // If there are fewer than max entries, it's always a high score
    if (gameEntries.length < LeaderboardManager.MAX_ENTRIES_PER_GAME) {
      return true;
    }

    // Check if score beats the lowest high score
    const lowestScore = gameEntries[gameEntries.length - 1]?.score || 0;
    return score > lowestScore;
  }

  public isAllTimeHighScore(score: number): boolean {
    // If there are fewer than max entries, it's always a high score
    if (this.data.allTimeLeaderboard.length < LeaderboardManager.MAX_ALL_TIME_ENTRIES) {
      return true;
    }

    // Check if score beats the lowest all-time high score
    const lowestScore = this.data.allTimeLeaderboard[this.data.allTimeLeaderboard.length - 1]?.score || 0;
    return score > lowestScore;
  }

  public getGameLeaderboard(gameId: string): LeaderboardEntry[] {
    return [...(this.data.gameLeaderboards[gameId] || [])];
  }

  public getAllTimeLeaderboard(): LeaderboardEntry[] {
    return [...this.data.allTimeLeaderboard];
  }

  public getAllLeaderboards(): { [gameId: string]: LeaderboardEntry[] } {
    const result: { [gameId: string]: LeaderboardEntry[] } = {};
    
    GAME_LIST.forEach(game => {
      result[game.id] = this.getGameLeaderboard(game.id);
    });

    return result;
  }

  public getHighScore(gameId: string): number {
    const entries = this.data.gameLeaderboards[gameId] || [];
    return entries.length > 0 ? entries[0].score : 0;
  }

  public getAllTimeHighScore(): number {
    return this.data.allTimeLeaderboard.length > 0 ? this.data.allTimeLeaderboard[0].score : 0;
  }

  public getRank(gameId: string, score: number): number {
    const entries = this.data.gameLeaderboards[gameId] || [];
    let rank = 1;
    
    for (const entry of entries) {
      if (score > entry.score) {
        break;
      }
      rank++;
    }
    
    return rank;
  }

  public getAllTimeRank(score: number): number {
    let rank = 1;
    
    for (const entry of this.data.allTimeLeaderboard) {
      if (score > entry.score) {
        break;
      }
      rank++;
    }
    
    return rank;
  }

  public clearData(): void {
    this.data = this.createEmptyData();
    this.saveToStorage();
  }

  public exportData(): string {
    return JSON.stringify(this.data, null, 2);
  }

  public importData(jsonData: string): boolean {
    try {
      const imported = JSON.parse(jsonData);
      this.data = this.validateAndMigrateData(imported);
      this.saveToStorage();
      return true;
    } catch (error) {
      console.error('Failed to import leaderboard data:', error);
      return false;
    }
  }

  public getStats(): {
    totalGames: number;
    totalScores: number;
    averageScore: number;
    topGame: string | null;
  } {
    let totalScores = 0;
    let totalPoints = 0;
    let topGame = null;
    let topGameScores = 0;

    GAME_LIST.forEach(game => {
      const entries = this.data.gameLeaderboards[game.id] || [];
      const gameScores = entries.length;
      totalScores += gameScores;
      
      entries.forEach(entry => {
        totalPoints += entry.score;
      });

      if (gameScores > topGameScores) {
        topGameScores = gameScores;
        topGame = game.title;
      }
    });

    return {
      totalGames: GAME_LIST.length,
      totalScores,
      averageScore: totalScores > 0 ? Math.round(totalPoints / totalScores) : 0,
      topGame
    };
  }
}